// Considered: DialogWrapper — rejected because the contract mounts this INSIDE the Placement
// block (`place > dialog`, `type-composer.jsx:1111-1160`), as a disclosure the author reads
// while still looking at the parent they just picked. A portaled modal would leave the block's
// container entirely and cover the choice it is explaining. See spec Design System Reuse Audit.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import './InheritanceManifestPanelMolecule.scss';

/** One line of the manifest: a category of machinery the parent brings, and what lands. */
export interface InheritanceManifestRow {
  /** Stable key — the category (`aspects`, `perspectives`, `arcs`, `properties`). */
  kind: string;
  label: string;
  items: string[];
  /**
   * REPLACE semantics rather than merge (default arcs) — worth saying out loud,
   * because the reader's mental model of inheritance here is additive.
   */
  replaces?: boolean;
}

export interface InheritanceManifestPanelMoleculeProps {
  /** The family whose machinery this enumerates. The manifest never renders without one. */
  parentName: string;
  rows: InheritanceManifestRow[];
  /** Needs the parent's own attachments already answer, so they are not the author's to set. */
  answeredNeeds: number;
  perspectivesArriving?: string[];
  arcsArriving?: string[];
  /**
   * The disclosure's initial state. Open by default — the manifest exists to be
   * read at the moment of choosing, and a collapsed acknowledgement is one the
   * author can dismiss without having seen anything.
   */
  defaultOpen?: boolean;
  /** True once the parent is this type's settled parent — the button then just closes. */
  alreadyItsParent?: boolean;
  onAcknowledge?: () => void;
  className?: string;
}

/**
 * *What &lt;parent&gt; brings* — the acknowledgement at the moment of choosing a family
 * (`type-composer.jsx:1111-1160`).
 *
 * The brief's asymmetric warning: a WRONG family is worse than no family, because the
 * machinery arrives silently and KEEPS arriving. So this enumerates what lands, says which
 * needs it answers for you, and states that future changes flow down — it is an
 * acknowledgement, not a summary.
 */
const InheritanceManifestPanelMolecule: React.FC<InheritanceManifestPanelMoleculeProps> = ({
  parentName,
  rows,
  answeredNeeds,
  perspectivesArriving = [],
  arcsArriving = [],
  defaultOpen = true,
  alreadyItsParent = false,
  onAcknowledge,
  className = '',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const summary = rows.map((row) => row.label).join(' · ');

  /*
    `data-kind="dialog"` is the contract's name for this datum, not an ARIA role: the
    manifest is a disclosure, not a modal. It has no scrim and takes no focus, so it
    owes no focus trap — the toggle below is a real button carrying `aria-expanded`.
  */
  return (
    <div
      className={['inheritance-manifest', className].filter(Boolean).join(' ')}
      data-datum="place.dialog"
      data-kind="dialog"
      data-testid="inheritance-manifest"
    >
      <button
        type="button"
        className="inheritance-manifest__head"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        data-datum="place.what"
        data-kind="button"
      >
        <i className="pi pi-box" aria-hidden="true" />
        <strong className="inheritance-manifest__title" data-datum="place.what#2" data-kind="strong">
          {t('studio.typeEditor.placement.manifestTitle', { parent: parentName })}
        </strong>
        <span className="inheritance-manifest__summary" data-datum="place.nothing-yet" data-kind="hint">
          {summary || t('studio.typeEditor.placement.manifestNothingYet')}
        </span>
        <i className={open ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
      </button>

      {open && (
        <div className="inheritance-manifest__body">
          {rows.map((row) => (
            <div
              key={row.kind}
              className="inheritance-manifest__row"
              data-container="each(rows)"
              data-datum="place.kv-row"
              data-kind="kv-row"
            >
              <span className="inheritance-manifest__row-label" data-datum="place.m-label" data-kind="label">
                {row.label}
              </span>
              <span className="inheritance-manifest__row-items" data-datum="place.m-items-join" data-kind="text">
                {row.items.join(' · ')}
              </span>
              {row.replaces && (
                <span
                  className="inheritance-manifest__replaces"
                  data-datum="place.replaces-doesn-t-merge"
                  data-kind="note"
                >
                  {t('studio.typeEditor.placement.manifestReplaces')}
                </span>
              )}
            </div>
          ))}

          {/*
            Delivering what the block header promises — which needs are already
            answered — instead of counts. `place.need-is` and
            `place.no-needs-are-answered-by-this` are the two branches of one sentence.
          */}
          <div
            className="inheritance-manifest__needs"
            data-datum="place.the-aspects"
            data-kind="hint"
          >
            <i className="pi pi-key" aria-hidden="true" />
            <span className="inheritance-manifest__needs-text">
              {answeredNeeds > 0 ? (
                <>
                  <strong data-datum="place.need-is" data-kind="strong">
                    {t('studio.typeEditor.placement.manifestNeedsAnswered', { count: answeredNeeds })}
                  </strong>{' '}
                  {t('studio.typeEditor.placement.manifestAspects')} {parentName}{' '}
                  {t('studio.typeEditor.placement.manifestAspectsSettled')}
                </>
              ) : (
                <>
                  <strong data-datum="place.no-needs-are-answered-by-this" data-kind="strong">
                    {t('studio.typeEditor.placement.manifestNoNeeds')}
                  </strong>{' '}
                  {t('studio.typeEditor.placement.manifestAllYours')}
                </>
              )}
              {perspectivesArriving.length > 0 && (
                <>
                  {' '}
                  {t('studio.typeEditor.placement.manifestPerspectivesLine', {
                    count: perspectivesArriving.length,
                    names: perspectivesArriving.slice(0, 3).join(', '),
                  })}
                </>
              )}
              {arcsArriving.length > 0 && (
                <>
                  {' '}
                  {arcsArriving.join(t('studio.typeEditor.placement.manifestArcJoin'))}{' '}
                  {t('studio.typeEditor.placement.manifestArc')}
                </>
              )}
            </span>
          </div>

          <div
            className="inheritance-manifest__warn"
            data-datum="place.machinery-arrives-now-and-keeps-arriving"
            data-kind="callout"
          >
            <i className="pi pi-sitemap" aria-hidden="true" />
            <span>
              <strong data-datum="place.its-future-changes-flow-down" data-kind="strong">
                {t('studio.typeEditor.placement.manifestFlowsDown')}
              </strong>{' '}
              {t('studio.typeEditor.placement.manifestKeepsArriving')}
            </span>
          </div>

          <button
            type="button"
            className="inheritance-manifest__ack"
            data-datum="place.i-ve-read-what-arrives"
            data-testid="inheritance-manifest-acknowledge"
            onClick={() => onAcknowledge?.()}
          >
            {alreadyItsParent
              ? t('studio.typeEditor.placement.manifestClose')
              : t('studio.typeEditor.placement.manifestAcknowledge')}
          </button>
        </div>
      )}
    </div>
  );
};

export default InheritanceManifestPanelMolecule;
